import { useMutation } from "@tanstack/react-query";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { authClient } from "@lib/okta";
import { CONFIG } from "@lib/config";
import { API_ROUTES } from "../../constants";
import type { SendMessageRequest } from "./types";

/**
 * Hook to send a message with streaming response
 */
export const useSendStreamingMessage = () => {
  return useMutation({
    retry: false,
    mutationFn: async ({
      chatId,
      message,
      parentId,
      onContent,
      onComplete,
      onError,
    }: {
      chatId: string;
      message: string;
      parentId?: string;
      onContent?: (content: string) => void;
      onComplete?: (messageId: string) => void;
      onError?: (error: string) => void;
    }) => {
      const payload: SendMessageRequest = {
        content: message,
        parent_id: parentId || null,
      };

      return new Promise<{ id: string; content: string }>((resolve, reject) => {
        let messageId = "";
        let fullContent = "";

        const accessToken = authClient.getAccessToken();

        fetchEventSource(
          `${CONFIG.AI_ROUTER_API}${API_ROUTES.omniSearch.chat(chatId)}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            },
            body: JSON.stringify(payload),
            onopen(response) {
              return new Promise((resolveOpen, rejectOpen) => {
                if (
                  response.ok &&
                  response.headers
                    .get("content-type")
                    ?.includes("text/event-stream")
                ) {
                  resolveOpen();
                } else {
                  rejectOpen(
                    new Error(`HTTP ${response.status}: ${response.statusText}`)
                  );
                }
              });
            },
            onmessage(event) {
              try {
                const data = JSON.parse(event.data) as {
                  content?: string | null;
                  id?: string | null;
                };

                const { event: eventType } = event;

                if (data.id && !messageId) {
                  messageId = data.id;
                }

                // Guard - ensure content is defined AND eventType is a message
                if (data.content && eventType === "message") {
                  fullContent += data.content;
                  onContent?.(data.content);
                }
              } catch (parseError) {
                console.error("Failed to parse stream data:", parseError);
              }
            },
            onerror(err) {
              const errorMessage =
                err instanceof Error ? err.message : "Stream failed";
              onError?.(errorMessage);
              // Throw the error to stop automatic retries by fetchEventSource
              throw new Error(errorMessage);
            },
            onclose() {
              if (messageId) {
                onComplete?.(messageId);
                resolve({ id: messageId, content: fullContent });
              } else {
                // Don't throw error on close, this can cause unnecessary retries
                onError?.("Stream closed without message ID");
                reject(new Error("Stream closed without message ID"));
              }
            },
          }
        ).catch((err) => {
          const errorMessage =
            err instanceof Error ? err.message : "Stream failed";
          onError?.(errorMessage);
          reject(err instanceof Error ? err : new Error(errorMessage));
        });
      });
    },
  });
};
